import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getCategories } from '@/lib/api';
import { BUSINESS } from '@/data/site';

const ECWID_STORE_ID = '125430003';
let scriptPromise = null;
let ecwidInited = false;
let browserStarted = false;
let pageScrollBound = false;

export function loadEcwidScript() {
  if (window.Ecwid && window.Ecwid.OnAPILoaded) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = `https://app.ecwid.com/script.js?${ECWID_STORE_ID}&data_platform=code`;
    s.charset = 'utf-8';
    s.setAttribute('data-cfasync', 'false');
    s.onload = () => {
      const t = setInterval(() => {
        if (window.Ecwid && window.Ecwid.OnAPILoaded) {
          clearInterval(t);
          resolve();
        }
      }, 100);
    };
    document.body.appendChild(s);
  });
  return scriptPromise;
}

export function initEcwid() {
  return loadEcwidScript().then(() => {
    if (!ecwidInited && window.Ecwid && window.Ecwid.init) {
      ecwidInited = true;
      window.Ecwid.init();
    }
  });
}

/**
 * Link that forces a full page load for Ecwid hash URLs (anything '#!...').
 * A full load lets Ecwid read the hash natively on boot — the only 100%
 * reliable way to land on a category/product/cart deep link. SPA pushState
 * navigation to these URLs leaves Ecwid's pre-initialised storefront in a
 * broken half-state (blank popup / dimmed page until refresh).
 */
export function EcwidLink({ to, children, ...rest }) {
  if (typeof to === 'string' && to.includes('#!')) {
    return <a href={to} {...rest}>{children}</a>;
  }
  return <Link to={to} {...rest}>{children}</Link>;
}

/* ---------- $ minimum-order progress bar inside Ecwid's native cart ---------- */
let minOrderBound = false;
let lastCartTotal = null;

function domTotal() {
  const store = document.getElementById('my-store-125430003');
  if (!store) return null;
  const label = Array.from(store.querySelectorAll('div, span, td'))
    .find((el) => el.children.length === 0 && /^\s*(subtotal|total)\s*$/i.test((el.textContent || '').trim()));
  if (label && label.parentElement) {
    const m = (label.parentElement.textContent || '').match(/\$\s*([\d,]+(?:\.\d{2})?)/);
    if (m) return parseFloat(m[1].replace(/,/g, ''));
  }
  return null;
}

function withCartTotal(cb) {
  let done = false;
  const finish = (t) => { if (!done) { done = true; cb(t); } };
  try {
    if (window.Ecwid && window.Ecwid.Cart && window.Ecwid.Cart.get) {
      window.Ecwid.Cart.get((cart) => {
        const t = cart && typeof cart.subtotal === 'number' ? cart.subtotal
          : cart && typeof cart.total === 'number' ? cart.total : null;
        finish(t);
      });
      setTimeout(() => finish(domTotal()), 900);
      return;
    }
  } catch (e) { /* fall through */ }
  finish(domTotal());
}

function onCartPage() {
  return (window.location.hash || '').includes('~/cart');
}

function ensureMinOrderUI() {
  const store = document.getElementById('my-store-125430003');
  const existing = document.getElementById('min-order-bar');
  if (!store || !onCartPage()) {
    if (existing) existing.remove();
    lastCartTotal = null;
    return;
  }
  withCartTotal((total) => {
    if (total === null || total === undefined || !onCartPage()) return;
    const btn = Array.from(store.querySelectorAll('button'))
      .find((b) => /checkout/i.test((b.textContent || '').trim()) || b.dataset.minBlocked === '1');
    if (!btn) return;
    if (existing && lastCartTotal === total) return; // nothing changed
    lastCartTotal = total;

    const min = BUSINESS.minOrder;
    const remaining = Math.max(0, min - total);
    const pct = Math.min(100, (total / min) * 100);
    const met = remaining <= 0;

    let bar = existing;
    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'min-order-bar';
      bar.setAttribute('data-testid', 'min-order-bar');
      btn.parentElement.insertBefore(bar, btn);
    }
    bar.style.cssText = 'margin:14px 0 12px;font-family:inherit;';
    bar.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px;font-size:13px;">
      <span data-testid="min-order-msg" style="font-weight:700;color:${met ? '#2f5233' : '#c0392b'};">
        ${met ? `You've reached the $${min} minimum — nice!` : `Add $${remaining.toFixed(2)} more — $${min} minimum order`}
      </span>
      <span data-testid="min-order-fraction" style="color:#8a8a84;white-space:nowrap;">$${total.toFixed(2)} / $${min.toFixed(2)}</span>
    </div>
    <div style="margin-top:8px;height:8px;border-radius:9999px;background:#e8e6df;overflow:hidden;">
      <div data-testid="min-order-fill" style="height:100%;width:${pct}%;border-radius:9999px;background:${met ? '#2f5233' : '#c0392b'};transition:width .5s ease;"></div>
    </div>`;

    // checkout button: swap label + mark blocked below minimum
    if (!btn.dataset.origLabel) btn.dataset.origLabel = (btn.textContent || '').trim();
    if (met) {
      btn.textContent = btn.dataset.origLabel;
      btn.style.opacity = '';
      btn.style.cursor = '';
      btn.dataset.minBlocked = '';
    } else {
      btn.textContent = `$${min} MINIMUM ORDER`;
      btn.style.opacity = '0.6';
      btn.style.cursor = 'not-allowed';
      btn.dataset.minBlocked = '1';
    }
  });
}

function bindMinOrderGuard() {
  if (minOrderBound) return;
  minOrderBound = true;
  const kick = (delay) => setTimeout(ensureMinOrderUI, delay);
  window.Ecwid.OnAPILoaded.add(() => {
    try {
      window.Ecwid.OnPageSwitched.add(() => kick(350));
      if (window.Ecwid.OnCartChanged) window.Ecwid.OnCartChanged.add(() => kick(150));
    } catch (e) { /* noop */ }
    kick(600);
  });
  // Ecwid re-renders its cart DOM often — re-assert the bar when it disappears
  // or the subtotal changes; cheap check, debounced.
  let obsTimer = null;
  const store = () => document.getElementById('my-store-125430003');
  const attachObserver = () => {
    const el = store();
    if (!el) return;
    new MutationObserver(() => {
      if (!onCartPage()) return;
      clearTimeout(obsTimer);
      obsTimer = setTimeout(ensureMinOrderUI, 120);
    }).observe(el, { childList: true, subtree: true });
  };
  // block checkout clicks while below the minimum (capture phase, before Ecwid)
  document.addEventListener('click', (e) => {
    if (!onCartPage()) return;
    const b = e.target && e.target.closest ? e.target.closest('button') : null;
    if (!b || (b.dataset.minBlocked !== '1')) return;
    e.preventDefault();
    e.stopPropagation();
    const bar = document.getElementById('min-order-bar');
    if (bar && bar.animate) {
      bar.animate(
        [{ transform: 'translateX(0)' }, { transform: 'translateX(-5px)' }, { transform: 'translateX(5px)' }, { transform: 'translateX(0)' }],
        { duration: 240 }
      );
    }
  }, true);
  window.Ecwid.OnAPILoaded.add(attachObserver);
}


/** Canonical Ecwid category URL for our SPA: '/shop' path + Ecwid's friendly hash.
 * Navigating with the hash in ONE history entry lets Ecwid follow natively
 * (no extra pushState -> single Back press works). */
export function ecwidCategoryUrl(cat) {
  if (!cat) return '/shop';
  const slugName = (cat.name || '').trim().replace(/\s+/g, '-');
  return `/shop#!/${slugName}/c/${cat.id}`;
}

/** Floating shopping-bag bubble (bottom-right) with live item count. */
export function EcwidMinicart() {
  useEffect(() => { initEcwid(); }, []);
  return <div className="ec-cart-widget" data-testid="ecwid-minicart"></div>;
}

/** Single featured product card with options + add-to-bag (native Ecwid). */
export function EcwidSingleProduct({ productId, title, price }) {
  useEffect(() => {
    let alive = true;
    initEcwid().then(() => {
      if (alive && window.xProduct) window.xProduct();
    });
    return () => { alive = false; };
  }, [productId]);
  return (
    <div
      className={`ecsp ecsp-SingleProduct-v2 ecsp-SingleProduct-v2-bordered ecsp-SingleProduct-v2-centered ecsp-Product ec-Product-${productId}`}
      itemScope
      itemType="http://schema.org/Product"
      data-single-product-id={String(productId)}
      data-testid={`ecwid-single-product-${productId}`}
    >
      <div itemProp="image"></div>
      <div className="ecsp-title" itemProp="name" content={title}></div>
      <div itemType="http://schema.org/Offer" itemScope itemProp="offers">
        <div className="ecsp-productBrowser-price ecsp-price" itemProp="price" content={String(price)} data-spw-price-location="button">
          <div itemProp="priceCurrency" content="USD"></div>
        </div>
      </div>
      <div {...{ customprop: 'options' }}></div>
      <div {...{ customprop: 'qty' }}></div>
      <div {...{ customprop: 'addtobag' }}></div>
      <div {...{ customprop: 'vatinprice' }}></div>
    </div>
  );
}

/**
 * ONE persistent Ecwid product-browser, mounted app-wide and shown only on
 * /shop* and /checkout. Mounting/unmounting per route breaks Ecwid (stale DOM,
 * minicart injection, nextSibling crashes), so the container never unmounts.
 *
 * Navigation contract:
 * - Deep links ('#!...' already in URL) are respected untouched.
 * - Otherwise we replaceState Ecwid's canonical friendly hash BEFORE init —
 *   no extra history entry, so one Back press returns home.
 * - Route changes while running: replaceState + a manual hashchange dispatch,
 *   which Ecwid follows WITHOUT pushing history (no openPage push, no crashes).
 * - Links carrying an Ecwid hash ('#!...') never reach this component as SPA
 *   navigations: they are plain anchors (see EcwidLink) that full-load the
 *   page, so Ecwid always boots cleanly on the target page.
 */
export function EcwidStoreShell() {
  const location = useLocation();
  const { data: categories } = useQuery({ queryKey: ['categories'], queryFn: getCategories, staleTime: 120000 });

  const isShop = location.pathname.startsWith('/shop');
  const isCheckout = location.pathname.startsWith('/checkout');
  const visible = isShop || isCheckout;
  const slug = isShop ? (location.pathname.split('/')[2] || '') : '';

  // Ecwid-internal navigation (clicking a category/product/cart INSIDE the
  // store) only changes the hash, so the route-level ScrollToTop never fires.
  // Bind the official OnPageSwitched event once: whenever the storefront swaps
  // pages while we're on /shop or /checkout, bring the store's top back into
  // view if the user was scrolled deep inside it.
  useEffect(() => {
    initEcwid().then(() => {
      if (pageScrollBound || !window.Ecwid || !window.Ecwid.OnPageSwitched) return;
      pageScrollBound = true;
      window.Ecwid.OnPageSwitched.add(() => {
        try {
          const p = window.location.pathname;
          if (!p.startsWith('/shop') && !p.startsWith('/checkout')) return;
          const el = document.querySelector('[data-testid="ecwid-store-shell"]');
          if (!el) return;
          const y = el.getBoundingClientRect().top + window.scrollY - 80; // clear the fixed header
          if (window.scrollY > y) {
            if (window.__lenis) window.__lenis.scrollTo(y, { immediate: true, force: true });
            window.scrollTo(0, y);
            document.documentElement.scrollTop = y;
            document.body.scrollTop = y;
          }
        } catch (e) { /* never let a scroll helper break the store */ }
      });
    });
  }, []);

  useEffect(() => {
    if (!visible) return;

    const current = window.location.hash;
    let desired;
    if (current.startsWith('#!')) {
      desired = current; // deep link — leave it alone
    } else if (isCheckout) {
      desired = '#!/~/cart';
    } else if (slug) {
      if (!categories) return; // wait for the slug map, effect re-runs when it lands
      const cat = categories.find((c) => c.slug === slug);
      if (!cat) return;
      desired = `#!/${cat.name.trim().replace(/\s+/g, '-')}/c/${cat.id}`;
    } else {
      desired = '#!/~/';
    }

    if (!browserStarted) {
      browserStarted = true;
      if (window.location.hash !== desired) {
        window.history.replaceState(null, '', `${window.location.pathname}${desired}`);
      }
      initEcwid().then(() => {
        bindMinOrderGuard();
        if (window.xProductBrowser) {
          window.xProductBrowser(
            'categoriesPerRow=3',
            'views=grid(20,3) list(60) table(60)',
            'categoryView=grid',
            'searchView=list',
            'id=my-store-125430003'
          );
          // Ecwid may have already resolved its storefront state to the root page
          // (the minicart initialises it app-wide) — force a re-read of our hash
          // once the browser has mounted.
          setTimeout(() => {
            if (window.location.hash !== desired) {
              window.history.replaceState(null, '', `${window.location.pathname}${desired}`);
              window.dispatchEvent(new HashChangeEvent('hashchange'));
              return;
            }
            // deep link: hash was already correct, but Ecwid booted on the root
            // page and a same-value hashchange is a no-op — navigate explicitly
            const m = window.location.hash.match(/\/c\/(\d+)/);
            if (m && window.Ecwid && window.Ecwid.OnAPILoaded && window.Ecwid.openPage) {
              window.Ecwid.OnAPILoaded.add(() => {
                try { window.Ecwid.openPage('category', { id: Number(m[1]) }); } catch (e) { console.warn('Ecwid deep-link nav failed', e); }
              });
            } else {
              window.dispatchEvent(new HashChangeEvent('hashchange'));
            }
          }, 1500);
        }
      });
      return;
    }

    if (window.location.hash !== desired) {
      window.history.replaceState(null, '', `${window.location.pathname}${desired}`);
      window.dispatchEvent(new HashChangeEvent('hashchange')); // Ecwid follows without pushing history
    }
    // location.hash in deps: clicking SHOP while already inside a store category
    // removes the Ecwid hash via React Router — the pathname doesn't change, so
    // without this dep the effect never re-runs and the store stays stuck in the
    // category. With it, we reset Ecwid to its root ('#!/~/').
  }, [visible, isShop, isCheckout, slug, categories, location.hash]);

  return (
    <div
      data-testid="ecwid-store-shell"
      style={visible ? undefined : { visibility: 'hidden', height: 0, overflow: 'hidden', pointerEvents: 'none' }}
      aria-hidden={!visible}
    >
      <div className="mx-auto max-w-7xl px-6 pb-28">
        <div id="my-store-125430003" data-testid="ecwid-store-browser" className="min-h-[60vh]"></div>
        {isShop && (
          <p className="mt-10 border border-ink/10 bg-parchment p-5 text-center text-xs leading-relaxed text-sage">
            ${BUSINESS.minOrder} minimum order · Secure checkout by Ecwid · Adults 21+ · Delivery orders: electronic payment only — cash not accepted for delivery
          </p>
        )}
      </div>
    </div>
  );
}
